// ============================================
// HEROSHHI - SIGN IN
// File: src/pages/auth/LoginPage.jsx
// Same logic as before (login, two-step code, backup codes, reset/verified
// notices); the look comes from AuthLayout.jsx.
// ============================================

import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import config from '../../config';
import { twoFactorAPI } from '../../services/api';
import AuthLayout, { Field, Notice, PasswordInput, SubmitButton } from './AuthLayout';
import EmailCodeStep from './EmailCodeStep';

const LoginPage = ({ onLogin }) => {
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // two-step sign-in
  const [requires2FA, setRequires2FA] = useState(false);
  const [codeEmail, setCodeEmail] = useState('');   // ✉️ a new account that hasn't confirmed its email
  const [pendingUserId, setPendingUserId] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [verifying2FA, setVerifying2FA] = useState(false);

  // notices from other screens
  const passwordReset = searchParams.get('reset') === 'success';
  const emailVerified = searchParams.get('verified') === 'true';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${config.API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });
      const data = await response.json();
      if (data.success) {
        if (data.requires2FA) {
          setPendingUserId(data.userId);
          setRequires2FA(true);
          setLoading(false);
          return;
        }
        localStorage.setItem('token', data.token);
        onLogin(data.user, data.token);
      } else if (data.needsVerification) {
        setCodeEmail(data.email || formData.email);
      } else {
        setError(data.message || 'That email and password don\'t match. Check them and try again.');
      }
    } catch (err) {
      setError('We couldn\'t reach Heroshhi. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    const code = twoFactorCode.replace(/\s/g, '');
    if (!code) { setError('Enter the code first.'); return; }
    setVerifying2FA(true);
    setError('');
    try {
      const response = await twoFactorAPI.verify(pendingUserId, code, isBackupCode);
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.warning) alert(response.data.warning);   // few backup codes left
        onLogin(response.data.user, response.data.token);
      } else {
        setError(response.data.message || 'That code didn\'t work. Try the newest one from your app.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'That code didn\'t work. Try the newest one from your app.');
    } finally {
      setVerifying2FA(false);
    }
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setPendingUserId(null);
    setTwoFactorCode('');
    setIsBackupCode(false);
    setError('');
  };

  // ---------- a new account confirming its email ----------
  if (codeEmail) {
    return (
      <EmailCodeStep
        email={codeEmail}
        backLabel="Back to sign in"
        onBack={() => setCodeEmail('')}
        onVerified={(user, token) => { localStorage.setItem('token', token); onLogin(user, token); }}
      />
    );
  }

  // ---------- step 2: the code ----------
  if (requires2FA) {
    return (
      <AuthLayout>
        <button type="button" className="hs-back hs-link-btn" style={{ textDecoration: 'none' }} onClick={cancel2FA}>Back to sign in</button>
        <h1>Enter your code</h1>
        <p className="hs-auth-sub">
          {isBackupCode
            ? 'Type one of your 8-character backup codes.'
            : 'Open your authenticator app and type the 6-digit code for Heroshhi.'}
        </p>
        {error && <Notice>{error}</Notice>}
        <form onSubmit={handle2FAVerify} noValidate>
          <Field id="code" label={isBackupCode ? 'Backup code' : 'Code'}>
            <input
              id="code"
              type="text"
              inputMode={isBackupCode ? 'text' : 'numeric'}
              value={twoFactorCode}
              onChange={(e) => {
                const value = isBackupCode
                  ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
                  : e.target.value.replace(/\D/g, '').slice(0, 6);
                setTwoFactorCode(value);
                setError('');
              }}
              placeholder={isBackupCode ? 'XXXXXXXX' : '000000'}
              className="hs-input hs-code"
              autoFocus
              autoComplete="one-time-code"
            />
          </Field>
          <SubmitButton loading={verifying2FA} loadingText="Checking" disabled={!twoFactorCode}>Continue</SubmitButton>
        </form>
        <p className="hs-auth-foot">
          <button type="button" className="hs-link-btn" onClick={() => { setIsBackupCode(!isBackupCode); setTwoFactorCode(''); setError(''); }}>
            {isBackupCode ? 'Use the code from your app instead' : 'Use a backup code instead'}
          </button>
        </p>
      </AuthLayout>
    );
  }

  // ---------- step 1: email and password ----------
  return (
    <AuthLayout>
      <h1>Welcome back</h1>
      <p className="hs-auth-sub">Sign in to see which leads need you today.</p>

      {passwordReset && <Notice kind="ok">Your password has been changed. Sign in with the new one.</Notice>}
      {emailVerified && <Notice kind="ok">Your email is confirmed. You can sign in now.</Notice>}
      {error && <Notice>{error}</Notice>}

      <form onSubmit={handleSubmit}>
        <Field id="email" label="Email">
          <input id="email" type="email" name="email" value={formData.email} onChange={handleChange}
            className="hs-input" placeholder="you@yourbusiness.com" required autoComplete="email" autoFocus />
        </Field>
        <Field id="password" label="Password" aside={<Link to="/forgot-password">Forgot it?</Link>}>
          <PasswordInput id="password" name="password" value={formData.password} onChange={handleChange}
            placeholder="Your password" autoComplete="current-password" />
        </Field>
        <SubmitButton loading={loading} loadingText="Signing in">Sign in</SubmitButton>
      </form>

      <p className="hs-auth-foot">New to Heroshhi? <Link to="/signup">Create a free account</Link></p>
    </AuthLayout>
  );
};

export default LoginPage;